import { useState } from 'react';
import axios from 'axios';
import { Alert, Box, Button, Card, CardContent, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TENANT_ADMIN_SET_PASSWORD } from '../../api';
import { apiPathUrl } from '../../utils/config';

const invitationApi = axios.create({
  baseURL: apiPathUrl,
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
});

const errorMessage = (error) => {
  const payload = error?.response?.data;
  if (typeof payload?.detail === 'string') return payload.detail;
  if (typeof payload?.message === 'string') return payload.message;
  const fieldError = Object.entries(payload || {}).find(([, value]) => Array.isArray(value) && value.length);
  return fieldError ? fieldError[1][0] : 'The password could not be set. Please request a new invitation.';
};

export default function TenantAdminSetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [values, setValues] = useState({ password: '', password_confirm: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [complete, setComplete] = useState(false);

  const update = (name) => (event) => {
    setValues((current) => ({ ...current, [name]: event.target.value }));
    setErrors((current) => ({ ...current, [name]: '', request: '' }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!token || submitting) return;
    const nextErrors = {};
    if (values.password.length < 8) nextErrors.password = 'Use at least 8 characters.';
    if (!values.password_confirm) nextErrors.password_confirm = 'Confirm your password.';
    else if (values.password !== values.password_confirm) nextErrors.password_confirm = 'Passwords do not match.';
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setSubmitting(true);
    setErrors({});
    try {
      await invitationApi.post(TENANT_ADMIN_SET_PASSWORD, {
        token,
        password: values.password,
        password_confirm: values.password_confirm,
      });
      setComplete(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error) {
      setErrors({ request: errorMessage(error) });
    } finally {
      setSubmitting(false);
    }
  };

  return <Box sx={{ minHeight: '100vh', px: 2, py: 5, display: 'grid', placeItems: 'center', bgcolor: '#f4f7fb' }}>
    <Card sx={{ width: '100%', maxWidth: 460, border: '1px solid #e2e8f0', borderRadius: 3, boxShadow: '0 14px 35px rgba(15,35,70,.08)' }}>
      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Typography sx={{ color: '#07154a', fontSize: 24, fontWeight: 850 }}>Set your password</Typography>
        <Typography sx={{ mt: .75, mb: 3, color: '#64748b', fontSize: 13 }}>Create a secure password for your BookCleany administrator account.</Typography>
        {!token && <Alert severity="error">This password setup link is invalid or incomplete. Ask the Super Admin to resend your invitation.</Alert>}
        {complete ? <>
          <Alert severity="success">Your password has been set successfully. You can now sign in.</Alert>
          <Button fullWidth variant="contained" onClick={() => navigate('/login', { replace: true })} sx={{ mt: 3, minHeight: 44 }}>Go to login</Button>
        </> : token && <Box component="form" onSubmit={submit} noValidate>
          {errors.request && <Alert severity="error" sx={{ mb: 2 }}>{errors.request}</Alert>}
          <TextField fullWidth required type="password" autoComplete="new-password" label="New password" value={values.password} onChange={update('password')} error={Boolean(errors.password)} helperText={errors.password || 'Use a strong password with at least 8 characters.'} sx={{ mb: 2 }} />
          <TextField fullWidth required type="password" autoComplete="new-password" label="Confirm password" value={values.password_confirm} onChange={update('password_confirm')} error={Boolean(errors.password_confirm)} helperText={errors.password_confirm} />
          <LoadingButton fullWidth type="submit" variant="contained" loading={submitting} sx={{ mt: 3, minHeight: 44 }}>Set password</LoadingButton>
        </Box>}
      </CardContent>
    </Card>
  </Box>;
}
